import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FiLogOut, FiEdit, FiTrash2, FiSave, FiX, FiChevronDown, FiChevronRight, FiMove, FiPlus } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ManageContent = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [addingChallenge, setAddingChallenge] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingChapterCategoryId, setAddingChapterCategoryId] = useState(null);
  const [draggedChapter, setDraggedChapter] = useState(null);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [newChallenge, setNewChallenge] = useState({
    question: '',
    type: 'multiple_choice',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: ''
  });
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    order_index: ''
  });
  const [newChapter, setNewChapter] = useState({
    title: '',
    description: '',
    content: '',
    video_url: '',
    video_middle_url: '',
    video_conclusion_url: '',
    order_index: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin-master/categories`);
      setCategories(response.data.categories);
      
      // Buscar capítulos de cada categoria
      const categoriesWithChapters = await Promise.all(
        response.data.categories.map(async (cat) => {
          try {
            const chaptersResponse = await axios.get(`${API_URL}/chapters?category_id=${cat.id}`);
            return { ...cat, chapters: chaptersResponse.data.chapters || [] };
          } catch (error) {
            return { ...cat, chapters: [] };
          }
        })
      );
      setCategories(categoriesWithChapters);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory({ ...category });
  };

  const handleSaveCategory = async () => {
    try {
      await axios.put(`${API_URL}/admin-master/categories/${editingCategory.id}`, {
        name: editingCategory.name,
        description: editingCategory.description,
        order_index: editingCategory.order_index
      });
      setEditingCategory(null);
      fetchCategories();
      alert('Categoria atualizada com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar categoria: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim() || !newCategory.order_index) {
      alert('Preencha nome e ordem da aula');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin-master/categories`, {
        name: newCategory.name,
        description: newCategory.description || '',
        order_index: parseInt(newCategory.order_index, 10)
      });
      setAddingCategory(false);
      setNewCategory({ name: '', description: '', order_index: '' });
      fetchCategories();
      alert('Aula criada com sucesso!');
    } catch (error) {
      alert('Erro ao criar aula: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditChapter = async (chapterId) => {
    try {
      const response = await axios.get(`${API_URL}/admin-master/chapters/${chapterId}`);
      setEditingChapter(response.data);
    } catch (error) {
      alert('Erro ao carregar capítulo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveChapter = async () => {
    try {
      await axios.put(`${API_URL}/admin-master/chapters/${editingChapter.chapter.id}`, {
        title: editingChapter.chapter.title,
        description: editingChapter.chapter.description,
        content: editingChapter.chapter.content,
        video_url: editingChapter.chapter.video_url,
        video_middle_url: editingChapter.chapter.video_middle_url,
        video_conclusion_url: editingChapter.chapter.video_conclusion_url,
        order_index: editingChapter.chapter.order_index
      });
      setEditingChapter(null);
      fetchCategories();
      alert('Capítulo atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar capítulo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleStartAddChapter = (category) => {
    const nextOrder = (category.chapters?.length || 0) + 1;
    setExpandedCategories(prev => ({
      ...prev,
      [category.id]: true
    }));
    setAddingChapterCategoryId(category.id);
    setNewChapter({
      title: '',
      description: '',
      content: '',
      video_url: '',
      video_middle_url: '',
      video_conclusion_url: '',
      order_index: nextOrder
    });
  };

  const handleAddChapter = async (categoryId) => {
    if (!newChapter.title.trim() || !newChapter.description.trim() || !newChapter.content.trim()) {
      alert('Preencha titulo, descricao e conteudo do capitulo');
      return;
    }

    if (!newChapter.order_index) {
      alert('Informe a ordem do capitulo');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin-master/chapters`, {
        category_id: categoryId,
        title: newChapter.title,
        description: newChapter.description,
        content: newChapter.content,
        video_url: newChapter.video_url || '',
        video_middle_url: newChapter.video_middle_url || '',
        video_conclusion_url: newChapter.video_conclusion_url || '',
        order_index: parseInt(newChapter.order_index, 10)
      });
      setAddingChapterCategoryId(null);
      setNewChapter({ title: '', description: '', content: '', video_url: '', video_middle_url: '', video_conclusion_url: '', order_index: '' });
      fetchCategories();
      alert('Capitulo criado com sucesso!');
    } catch (error) {
      alert('Erro ao criar capitulo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditChallenge = (challenge) => {
    setEditingChapter({
      ...editingChapter,
      challenges: editingChapter.challenges.map(c => 
        c.id === challenge.id ? { ...challenge, editing: true } : c
      )
    });
  };

  const handleSaveChallenge = async (challenge) => {
    try {
      await axios.put(`${API_URL}/admin-master/challenges/${challenge.id}`, {
        question: challenge.question,
        option_a: challenge.option_a,
        option_b: challenge.option_b,
        option_c: challenge.option_c,
        option_d: challenge.option_d,
        correct_answer: challenge.correct_answer,
        order_index: challenge.order_index
      });
      
      setEditingChapter({
        ...editingChapter,
        challenges: editingChapter.challenges.map(c => 
          c.id === challenge.id ? { ...c, editing: false } : c
        )
      });
      fetchCategories();
      alert('Desafio atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar desafio: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('Tem certeza que deseja deletar este desafio?')) return;

    try {
      await axios.delete(`${API_URL}/admin-master/challenges/${challengeId}`);
      setEditingChapter({
        ...editingChapter,
        challenges: editingChapter.challenges.filter(c => c.id !== challengeId)
      });
      fetchCategories();
      alert('Desafio deletado com sucesso!');
    } catch (error) {
      alert('Erro ao deletar desafio: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Tem certeza que deseja deletar este capítulo? Todos os desafios serão deletados também.')) return;

    try {
      await axios.delete(`${API_URL}/admin-master/chapters/${chapterId}`);
      setEditingChapter(null);
      fetchCategories();
      alert('Capítulo deletado com sucesso!');
    } catch (error) {
      alert('Erro ao deletar capítulo: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja deletar este tema? Todos os capítulos e desafios serão deletados também.')) return;

    try {
      await axios.delete(`${API_URL}/admin-master/categories/${categoryId}`);
      fetchCategories();
      alert('Tema deletado com sucesso!');
    } catch (error) {
      alert('Erro ao deletar tema: ' + (error.response?.data?.error || error.message));
    }
  };

  // Drag and drop para Categories (Temas)
  const handleCategoryDragStart = (e, categoryId) => {
    setDraggedCategory(categoryId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = async (e, targetCategoryId) => {
    e.preventDefault();
    
    if (!draggedCategory || draggedCategory === targetCategoryId) {
      setDraggedCategory(null);
      return;
    }

    const sortedCategories = [...categories].sort((a, b) => a.order_index - b.order_index);
    const draggedIndex = sortedCategories.findIndex(cat => cat.id === draggedCategory);
    const targetIndex = sortedCategories.findIndex(cat => cat.id === targetCategoryId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCategory(null);
      return;
    }

    // Reordenar categories
    const [removed] = sortedCategories.splice(draggedIndex, 1);
    sortedCategories.splice(targetIndex, 0, removed);

    // Atualizar order_index
    const updatedCategories = sortedCategories.map((cat, index) => ({
      ...cat,
      order_index: index + 1
    }));

    try {
      // Enviar nova ordem para o backend
      await axios.put(`${API_URL}/admin-master/categories/order`, {
        categories: updatedCategories.map(cat => ({ id: cat.id, order_index: cat.order_index }))
      });

      // Atualizar estado local
      setCategories(updatedCategories);

      alert('Ordem dos temas atualizada com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar ordem: ' + (error.response?.data?.error || error.message));
    } finally {
      setDraggedCategory(null);
    }
  };

  // Drag and drop para Chapters (Capítulos)
  const handleDragStart = (e, chapterId) => {
    setDraggedChapter(chapterId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetChapterId, categoryId) => {
    e.preventDefault();
    
    if (!draggedChapter || draggedChapter === targetChapterId) {
      setDraggedChapter(null);
      return;
    }

    const category = categories.find(cat => cat.id === categoryId);
    if (!category || !category.chapters) return;

    const chapters = [...category.chapters];
    const draggedIndex = chapters.findIndex(ch => ch.id === draggedChapter);
    const targetIndex = chapters.findIndex(ch => ch.id === targetChapterId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedChapter(null);
      return;
    }

    // Reordenar capítulos
    const [removed] = chapters.splice(draggedIndex, 1);
    chapters.splice(targetIndex, 0, removed);

    // Atualizar order_index
    const updatedChapters = chapters.map((ch, index) => ({
      ...ch,
      order_index: index + 1
    }));

    try {
      // Enviar nova ordem para o backend
      await axios.put(`${API_URL}/admin-master/chapters/order`, {
        chapters: updatedChapters.map(ch => ({ id: ch.id, order_index: ch.order_index }))
      });

      // Atualizar estado local
      setCategories(prevCategories =>
        prevCategories.map(cat =>
          cat.id === categoryId
            ? { ...cat, chapters: updatedChapters }
            : cat
        )
      );

      alert('Ordem dos capítulos atualizada com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar ordem: ' + (error.response?.data?.error || error.message));
    } finally {
      setDraggedChapter(null);
    }
  };

  const handleAddChallenge = async (chapterId) => {
    if (!newChallenge.question.trim()) {
      alert('A questão é obrigatória!');
      return;
    }

    if (newChallenge.type === 'multiple_choice') {
      if (!newChallenge.option_a || !newChallenge.option_b || !newChallenge.option_c || !newChallenge.option_d) {
        alert('Todas as opções são obrigatórias!');
        return;
      }
      if (!newChallenge.correct_answer || !['A', 'B', 'C', 'D'].includes(newChallenge.correct_answer.toUpperCase())) {
        alert('Resposta correta deve ser A, B, C ou D!');
        return;
      }
    }

    const challengeData = {
      question: newChallenge.question,
      type: newChallenge.type,
      order_index: editingChapter.challenges.length + 1,
      ...(newChallenge.type === 'multiple_choice' && {
        option_a: newChallenge.option_a,
        option_b: newChallenge.option_b,
        option_c: newChallenge.option_c,
        option_d: newChallenge.option_d,
        correct_answer: newChallenge.correct_answer.toUpperCase()
      })
    };

    try {
      await axios.post(`${API_URL}/admin-master/chapters/${chapterId}/challenge`, challengeData);
      // Recarregar o capítulo editado
      await handleEditChapter(chapterId);
      // Limpar formulário
      setAddingChallenge(null);
      setNewChallenge({
        question: '',
        type: 'multiple_choice',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_answer: ''
      });
      alert('Desafio adicionado com sucesso!');
    } catch (error) {
      alert('Erro ao adicionar desafio: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gerenciar Conteúdo</h1>
            <p className="text-sm text-gray-600">Admin Master - {user?.username}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              Voltar ao Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <FiLogOut />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Aulas</h2>
            <button
              onClick={() => setAddingCategory(!addingCategory)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            >
              <FiPlus />
              Adicionar Aula
            </button>
          </div>
          {addingCategory && (
            <div className="mt-4 bg-white border border-gray-200 rounded p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded"
                  placeholder="Nome da aula"
                />
                <input
                  type="text"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded"
                  placeholder="Descricao"
                />
                <input
                  type="number"
                  value={newCategory.order_index}
                  onChange={(e) => setNewCategory({ ...newCategory, order_index: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded"
                  placeholder="Ordem"
                />
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Salvar Aula
                </button>
                <button
                  onClick={() => {
                    setAddingCategory(false);
                    setNewCategory({ name: '', description: '', order_index: '' });
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Categories List */}
        <div className="space-y-4">
          {categories.sort((a, b) => a.order_index - b.order_index).map((category) => (
            <div 
              key={category.id} 
              className={`bg-white rounded-lg border border-gray-200 relative transition-all ${
                draggedCategory === category.id ? 'opacity-50 border-blue-400 bg-blue-50' : 'hover:border-blue-300'
              } ${(!editingCategory || editingCategory.id !== category.id) ? 'cursor-move' : ''}`}
              draggable={!editingCategory || editingCategory.id !== category.id}
              onDragStart={(e) => {
                e.stopPropagation();
                handleCategoryDragStart(e, category.id);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                handleCategoryDragOver(e);
              }}
              onDrop={(e) => {
                e.stopPropagation();
                handleCategoryDrop(e, category.id);
              }}
            >
              {/* Category Header */}
              <div className="p-4 border-b border-gray-200 relative">
                {(!editingCategory || editingCategory.id !== category.id) && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-gray-400 pointer-events-none">
                    <FiMove />
                    <span>Arraste</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => setExpandedCategories(prev => ({
                        ...prev,
                        [category.id]: !prev[category.id]
                      }))}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      {expandedCategories[category.id] ? <FiChevronDown /> : <FiChevronRight />}
                    </button>
                    {editingCategory?.id === category.id ? (
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          value={editingCategory.name}
                          onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={editingCategory.description || ''}
                          onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded"
                          placeholder="Descrição"
                        />
                        <input
                          type="number"
                          value={editingCategory.order_index}
                          onChange={(e) => setEditingCategory({ ...editingCategory, order_index: parseInt(e.target.value) })}
                          className="w-20 px-3 py-1 border border-gray-300 rounded"
                        />
                        <button
                          onClick={handleSaveCategory}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <FiSave />
                        </button>
                        <button
                          onClick={() => setEditingCategory(null)}
                          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        >
                          <FiX />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-800">{category.name}</h3>
                        <span className="text-sm text-gray-500">({category.chapters?.length || 0} capítulos)</span>
                        <div className="ml-auto flex gap-2">
                          <button
                            onClick={() => handleStartAddChapter(category)}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                          >
                            <FiPlus />
                            Capitulo
                          </button>
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <FiEdit />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                          >
                            <FiTrash2 />
                            Deletar
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Chapters List */}
              {expandedCategories[category.id] && (
                <div className="p-4 space-y-3">
                  {addingChapterCategoryId === category.id && (
                    <div className="border border-green-200 bg-green-50 rounded p-4">
                      <h4 className="font-semibold mb-3">Novo Capitulo</h4>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={newChapter.title}
                          onChange={(e) => setNewChapter({ ...newChapter, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          placeholder="Titulo"
                        />
                        <input
                          type="text"
                          value={newChapter.description}
                          onChange={(e) => setNewChapter({ ...newChapter, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          placeholder="Descricao"
                        />
                        <textarea
                          value={newChapter.content}
                          onChange={(e) => setNewChapter({ ...newChapter, content: e.target.value })}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          placeholder="Conteudo (Markdown)"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <input
                            type="url"
                            value={newChapter.video_url}
                            onChange={(e) => setNewChapter({ ...newChapter, video_url: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded"
                            placeholder="Introdução"
                          />
                          <input
                            type="url"
                            value={newChapter.video_middle_url}
                            onChange={(e) => setNewChapter({ ...newChapter, video_middle_url: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded"
                            placeholder="Mão na Massa"
                          />
                          <input
                            type="url"
                            value={newChapter.video_conclusion_url}
                            onChange={(e) => setNewChapter({ ...newChapter, video_conclusion_url: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded"
                            placeholder="Conclusão e Curiosidades"
                          />
                        </div>
                        <input
                          type="number"
                          value={newChapter.order_index}
                          onChange={(e) => setNewChapter({ ...newChapter, order_index: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          placeholder="Ordem"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAddChapter(category.id)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Salvar Capitulo
                          </button>
                          <button
                            onClick={() => {
                              setAddingChapterCategoryId(null);
                              setNewChapter({ title: '', description: '', content: '', video_url: '', order_index: '' });
                            }}
                            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {category.chapters?.sort((a, b) => a.order_index - b.order_index).map((chapter, index) => (
                    <div 
                      key={chapter.id} 
                      className={`border border-gray-200 rounded p-4 relative ${
                        draggedChapter === chapter.id ? 'opacity-50 border-blue-400 bg-blue-50' : 'hover:border-blue-300'
                      }`}
                      draggable={!editingChapter || editingChapter.chapter?.id !== chapter.id}
                      onDragStart={(e) => handleDragStart(e, chapter.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, chapter.id, category.id)}
                    >
                      {(!editingChapter || editingChapter.chapter?.id !== chapter.id) && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-gray-400">
                          <FiMove />
                          <span>Arraste</span>
                        </div>
                      )}
                      {editingChapter?.chapter?.id === chapter.id ? (
                        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                          <div>
                            <label className="block text-sm font-medium mb-1">Título</label>
                            <input
                              type="text"
                              value={editingChapter.chapter.title}
                              onChange={(e) => setEditingChapter({
                                ...editingChapter,
                                chapter: { ...editingChapter.chapter, title: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Descrição</label>
                            <input
                              type="text"
                              value={editingChapter.chapter.description || ''}
                              onChange={(e) => setEditingChapter({
                                ...editingChapter,
                                chapter: { ...editingChapter.chapter, description: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Conteúdo (Markdown)</label>
                            <textarea
                              value={editingChapter.chapter.content}
                              onChange={(e) => setEditingChapter({
                                ...editingChapter,
                                chapter: { ...editingChapter.chapter, content: e.target.value }
                              })}
                              rows={10}
                              className="w-full px-3 py-2 border border-gray-300 rounded resize-y"
                              style={{ minHeight: '200px', maxHeight: '400px' }}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">Introdução</label>
                              <input
                                type="url"
                                value={editingChapter.chapter.video_url || ''}
                                onChange={(e) => setEditingChapter({
                                  ...editingChapter,
                                  chapter: { ...editingChapter.chapter, video_url: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Mão na Massa</label>
                              <input
                                type="url"
                                value={editingChapter.chapter.video_middle_url || ''}
                                onChange={(e) => setEditingChapter({
                                  ...editingChapter,
                                  chapter: { ...editingChapter.chapter, video_middle_url: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Conclusão e Curiosidades</label>
                              <input
                                type="url"
                                value={editingChapter.chapter.video_conclusion_url || ''}
                                onChange={(e) => setEditingChapter({
                                  ...editingChapter,
                                  chapter: { ...editingChapter.chapter, video_conclusion_url: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Ordem</label>
                            <input
                              type="number"
                              value={editingChapter.chapter.order_index}
                              onChange={(e) => setEditingChapter({
                                ...editingChapter,
                                chapter: { ...editingChapter.chapter, order_index: parseInt(e.target.value) }
                              })}
                              className="w-20 px-3 py-2 border border-gray-300 rounded"
                            />
                          </div>

                          {/* Challenges */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold">Desafios:</h4>
                              <button
                                onClick={() => setAddingChallenge(chapter.id)}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                              >
                                <FiEdit />
                                Adicionar Desafio
                              </button>
                            </div>

                            {/* Formulário para adicionar novo desafio */}
                            {addingChallenge === chapter.id && (
                              <div className="border-2 border-green-300 rounded p-4 mb-4 bg-green-50">
                                <h5 className="font-semibold mb-3">Novo Desafio</h5>
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Tipo de Questão</label>
                                    <select
                                      value={newChallenge.type}
                                      onChange={(e) => setNewChallenge({ ...newChallenge, type: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded"
                                    >
                                      <option value="multiple_choice">Múltipla Escolha</option>
                                      <option value="essay">Discursiva</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Questão</label>
                                    <textarea
                                      value={newChallenge.question}
                                      onChange={(e) => setNewChallenge({ ...newChallenge, question: e.target.value })}
                                      rows={3}
                                      className="w-full px-3 py-2 border border-gray-300 rounded"
                                      placeholder="Digite a questão..."
                                    />
                                  </div>
                                  {newChallenge.type === 'multiple_choice' && (
                                    <>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-sm font-medium mb-1">Opção A</label>
                                          <input
                                            type="text"
                                            value={newChallenge.option_a}
                                            onChange={(e) => setNewChallenge({ ...newChallenge, option_a: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded"
                                            placeholder="Opção A"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium mb-1">Opção B</label>
                                          <input
                                            type="text"
                                            value={newChallenge.option_b}
                                            onChange={(e) => setNewChallenge({ ...newChallenge, option_b: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded"
                                            placeholder="Opção B"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium mb-1">Opção C</label>
                                          <input
                                            type="text"
                                            value={newChallenge.option_c}
                                            onChange={(e) => setNewChallenge({ ...newChallenge, option_c: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded"
                                            placeholder="Opção C"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium mb-1">Opção D</label>
                                          <input
                                            type="text"
                                            value={newChallenge.option_d}
                                            onChange={(e) => setNewChallenge({ ...newChallenge, option_d: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded"
                                            placeholder="Opção D"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium mb-1">Resposta Correta</label>
                                        <select
                                          value={newChallenge.correct_answer}
                                          onChange={(e) => setNewChallenge({ ...newChallenge, correct_answer: e.target.value })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded"
                                        >
                                          <option value="">Selecione...</option>
                                          <option value="A">A</option>
                                          <option value="B">B</option>
                                          <option value="C">C</option>
                                          <option value="D">D</option>
                                        </select>
                                      </div>
                                    </>
                                  )}
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleAddChallenge(chapter.id)}
                                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                      Salvar Desafio
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAddingChallenge(null);
                                        setNewChallenge({
                                          question: '',
                                          type: 'multiple_choice',
                                          option_a: '',
                                          option_b: '',
                                          option_c: '',
                                          option_d: '',
                                          correct_answer: ''
                                        });
                                      }}
                                      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {editingChapter.challenges?.map((challenge) => (
                              <div key={challenge.id} className="border border-gray-200 rounded p-3 mb-2">
                                {challenge.editing ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={challenge.question}
                                      onChange={(e) => setEditingChapter({
                                        ...editingChapter,
                                        challenges: editingChapter.challenges.map(c =>
                                          c.id === challenge.id ? { ...c, question: e.target.value } : c
                                        )
                                      })}
                                      rows={2}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      placeholder="Questão"
                                    />
                                    {challenge.type === 'multiple_choice' && (
                                      <>
                                        <div className="grid grid-cols-2 gap-2">
                                          <input
                                            type="text"
                                            value={challenge.option_a || ''}
                                            onChange={(e) => setEditingChapter({
                                              ...editingChapter,
                                              challenges: editingChapter.challenges.map(c =>
                                                c.id === challenge.id ? { ...c, option_a: e.target.value } : c
                                              )
                                            })}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            placeholder="Opção A"
                                          />
                                          <input
                                            type="text"
                                            value={challenge.option_b || ''}
                                            onChange={(e) => setEditingChapter({
                                              ...editingChapter,
                                              challenges: editingChapter.challenges.map(c =>
                                                c.id === challenge.id ? { ...c, option_b: e.target.value } : c
                                              )
                                            })}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            placeholder="Opção B"
                                          />
                                          <input
                                            type="text"
                                            value={challenge.option_c || ''}
                                            onChange={(e) => setEditingChapter({
                                              ...editingChapter,
                                              challenges: editingChapter.challenges.map(c =>
                                                c.id === challenge.id ? { ...c, option_c: e.target.value } : c
                                              )
                                            })}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            placeholder="Opção C"
                                          />
                                          <input
                                            type="text"
                                            value={challenge.option_d || ''}
                                            onChange={(e) => setEditingChapter({
                                              ...editingChapter,
                                              challenges: editingChapter.challenges.map(c =>
                                                c.id === challenge.id ? { ...c, option_d: e.target.value } : c
                                              )
                                            })}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            placeholder="Opção D"
                                          />
                                        </div>
                                        <select
                                          value={challenge.correct_answer || ''}
                                          onChange={(e) => setEditingChapter({
                                            ...editingChapter,
                                            challenges: editingChapter.challenges.map(c =>
                                              c.id === challenge.id ? { ...c, correct_answer: e.target.value } : c
                                            )
                                          })}
                                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                                        >
                                          <option value="">Resposta Correta</option>
                                          <option value="A">A</option>
                                          <option value="B">B</option>
                                          <option value="C">C</option>
                                          <option value="D">D</option>
                                        </select>
                                      </>
                                    )}
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleSaveChallenge(challenge)}
                                        className="px-2 py-1 bg-blue-600 text-white rounded text-sm"
                                      >
                                        Salvar
                                      </button>
                                      <button
                                        onClick={() => setEditingChapter({
                                          ...editingChapter,
                                          challenges: editingChapter.challenges.map(c =>
                                            c.id === challenge.id ? { ...c, editing: false } : c
                                          )
                                        })}
                                        className="px-2 py-1 bg-gray-200 rounded text-sm"
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium">{challenge.question}</p>
                                      {challenge.type === 'multiple_choice' && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          {challenge.option_a} | {challenge.option_b} | {challenge.option_c} | {challenge.option_d}
                                          <span className="ml-2 font-semibold">Resposta: {challenge.correct_answer}</span>
                                        </p>
                                      )}
                                      {challenge.type === 'essay' && (
                                        <span className="text-xs text-blue-600">Questão Discursiva</span>
                                      )}
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleEditChallenge(challenge)}
                                        className="px-2 py-1 bg-blue-600 text-white rounded text-sm"
                                      >
                                        <FiEdit />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteChallenge(challenge.id)}
                                        className="px-2 py-1 bg-red-600 text-white rounded text-sm"
                                      >
                                        <FiTrash2 />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={handleSaveChapter}
                              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Salvar Capítulo
                            </button>
                            <button
                              onClick={() => handleDeleteChapter(chapter.id)}
                              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Deletar Capítulo
                            </button>
                            <button
                              onClick={() => setEditingChapter(null)}
                              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{chapter.title}</h4>
                            <p className="text-sm text-gray-600">{chapter.description}</p>
                          </div>
                          <button
                            onClick={() => handleEditChapter(chapter.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <FiEdit />
                            Editar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default ManageContent;
